import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid2 as Grid, Paper, Typography, Box, Chip, IconButton } from '@mui/material';
import {
  AccountBalanceWallet,
  TrendingUp,
  Receipt,
  CreditCard,
  MoreVert,
} from '@mui/icons-material';
import StatsCard from '../Common/StatsCard';
import ChartCard from '../Common/ChartCard';
import {
  mockClientStats,
  mockMonthlyExpenses,
  formatCurrency,
  formatDate,
} from '../../utils/mockData';

const DashboardCliente: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {t('dashboard.welcomeBack')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('dashboard.activitySummary')}
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('dashboard.availableBalance')}
            value={formatCurrency(mockClientStats.balance)}
            subtitle={t('dashboard.pagaliWallet')}
            icon={<AccountBalanceWallet />}
            color="success"
            trend={{ value: 8.5, isPositive: true }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('dashboard.totalSpent')}
            value={formatCurrency(mockClientStats.totalSpent)}
            subtitle={t('dashboard.last30Days')}
            icon={<TrendingUp />}
            color="primary"
            trend={{ value: 12.3, isPositive: false }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('dashboard.transactions')}
            value={mockClientStats.transactions}
            subtitle={t('dashboard.thisMonth')}
            icon={<Receipt />}
            color="info"
            trend={{ value: 5.2, isPositive: true }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('dashboard.savedCards')}
            value={mockClientStats.savedCards}
            subtitle={t('dashboard.pendingPaymentsCount', { count: mockClientStats.pendingPayments })}
            icon={<CreditCard />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <ChartCard
            title={t('dashboard.monthlyExpenses')}
            subtitle={t('dashboard.last7Months')}
            data={mockMonthlyExpenses}
            type="area"
            dataKey="value"
            xAxisKey="month"
            color="#1976d2"
            height={300}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('dashboard.quickActions')}
            </Typography>
            <Box sx={{ mt: 3 }}>
              {[
                { label: t('dashboard.payBill'), color: 'primary' },
                { label: t('dashboard.transfer'), color: 'success' },
                { label: t('dashboard.buyEvent'), color: 'info' },
                { label: t('dashboard.viewInvoices'), color: 'warning' },
              ].map((action, index) => (
                <Chip
                  key={index}
                  label={action.label}
                  color={action.color as any}
                  sx={{ mr: 1, mb: 1 }}
                  clickable
                />
              ))}
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('dashboard.pendingPayments')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="warning.main">
                {mockClientStats.pendingPayments}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('dashboard.needAttention')}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t('dashboard.recentTransactions')}
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', fontWeight: 600 }}
          >
            {t('dashboard.viewAll')}
          </Typography>
        </Box>

        <Box>
          {mockClientStats.recentActivity.map((transaction) => (
            <Box
              key={transaction.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: transaction.amount > 0 ? 'success.light' : 'action.selected',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <Receipt fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    {transaction.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(transaction.date)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  color={transaction.amount > 0 ? 'success.main' : 'text.primary'}
                >
                  {formatCurrency(transaction.amount)}
                </Typography>
                <Chip
                  label={transaction.status === 'completed' ? t('dashboard.completed') : t('dashboard.pending')}
                  size="small"
                  color={transaction.status === 'completed' ? 'success' : 'warning'}
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <IconButton size="small" sx={{ ml: 1 }}>
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default DashboardCliente;
